import { CalendarAvailabilityDayContent } from "@/components/molecules/Calendar";
import { CalendarAvailabilityModifiers } from "@/components/molecules/Calendar/modifiers/calendar-availability/calendar-availability-utils";
import {
  DatePickerField,
  DatePickerFieldProps,
} from "@/components/molecules/DatePicker/DatePickerField";
import {
  normalizeTimeData,
  TimeData,
  TimeOptions,
  TimeSelectField,
  TimeSelectFieldProps,
} from "@/components/molecules/TimeSelect";
import { Breakpoints } from "@/foundations/breakpoints/breakpoints";
import { useWindowSize } from "@/lib/hooks/use-window-size";
import {
  BookingScheduleDate,
  BookingScheduleTime,
} from "@/lib/types/schedule-types";
import { addDays } from "date-fns";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  bookingScheduleToDate,
  dateToBookingSchedule,
  validateScheduleDate,
  validateScheduleTime,
} from "./schedule-utils";

export type ScheduleDayInputsProps = {
  name: string;
  datePickerProps?: Omit<DatePickerFieldProps, "name">;
  timeSelectProps?: TimeSelectFieldProps;
  getCalendarModifiers?: (
    date: Date,
    totalMonths?: number,
  ) => Promise<{
    modifiers: CalendarAvailabilityModifiers;
  }>;
  getTimeOptions?: (
    date: Date,
    startTimeIndex?: number,
  ) => Promise<BookingScheduleTime[]>;
  locale?: string;
  testId?: string;
  dayPickerDataTestingId?: string;
  minHours?: number;
};

export const ScheduleDayInputs = ({
  name,
  getCalendarModifiers,
  datePickerProps,
  timeSelectProps,
  testId,
  getTimeOptions,
  locale = "en-US",
  dayPickerDataTestingId,
  minHours,
}: ScheduleDayInputsProps) => {
  const { watch, setValue } = useFormContext();

  const fieldNames = useMemo(
    () => ({
      startDate: `${name}.start.date`,
      startTime: `${name}.start.time`,
      endDate: `${name}.end.date`,
      endTime: `${name}.end.time`,
    }),
    [name],
  );

  const [calendarPopoverOpen, setCalendarPopoverOpen] = useState(false);
  const [visibleMonths, setVisibleMonths] = useState(2);
  const [calendarModifiers, setCalendarModifiers] = useState(
    {} as CalendarAvailabilityModifiers,
  );
  const [timeOptions, setTimeOptions] = useState(
    getTimeOptions
      ? ({} as TimeOptions)
      : {
          start: TimeData,
          end: TimeData,
        },
  );
  const [isFetchingCalendarModifiers, setIsFetchingCalendarModifiers] =
    useState(false);
  const [isFetchingStartTimeOptions, setIsFetchingStartTimeOptions] =
    useState(false);
  const [isFetchingEndTimeOptions, setIsFetchingEndTimeOptions] =
    useState(false);

  const requestCalendarModifiers = useCallback(
    async (date: Date, totalMonths: number) => {
      if (getCalendarModifiers) {
        const { modifiers } = await getCalendarModifiers(date, totalMonths);
        setCalendarModifiers(modifiers);
        setIsFetchingCalendarModifiers(false);
      }
    },
    [getCalendarModifiers],
  );

  const debouncedRequestCalendarModifiers = useMemo(() => {
    return debounce(requestCalendarModifiers, 500);
  }, [requestCalendarModifiers]);

  const handleMonthChange = async (date: Date) => {
    if (!getCalendarModifiers) {
      return;
    }
    setIsFetchingCalendarModifiers(true);
    debouncedRequestCalendarModifiers(date, visibleMonths);
  };

  const windowDims = useWindowSize();
  const handleOpenChange = async (isOpen: boolean) => {
    setCalendarPopoverOpen(isOpen);
    if (!isOpen || !getCalendarModifiers) {
      return;
    }
    const visibleMonths = (windowDims.width || 0) < Breakpoints.SM ? 1 : 2;
    setVisibleMonths(visibleMonths);
    setIsFetchingCalendarModifiers(true);

    const dateValue = watch(fieldNames.startDate) as BookingScheduleDate;
    requestCalendarModifiers(
      bookingScheduleToDate(dateValue) || new Date(),
      visibleMonths,
    );
  };

  const autoSelectEndTime = () => {
    const startTime = watch(fieldNames.startTime);
    const startDate = watch(fieldNames.startDate);

    if (!startTime || !startDate || !timeOptions?.end?.length) {
      return;
    }

    const endTimeIndex = Math.min(
      startTime.time_index + minHours! * 2,
      timeOptions?.end[timeOptions?.end?.length - 1]?.time_index ?? Infinity,
    );

    const endTime = timeOptions?.end.find(
      (option) => option.time_index === endTimeIndex,
    );

    if (endTime) {
      const normalizedEndTime = normalizeTimeData([endTime])[0];
      setValue(fieldNames.endTime, normalizedEndTime);
      updateEndDate(startDate, normalizedEndTime);
    }
  };

  const prevEndTimeOptions = useRef(timeOptions?.end);
  useEffect(() => {
    if (
      !minHours ||
      !getTimeOptions ||
      isEqual(prevEndTimeOptions.current, timeOptions?.end)
    ) {
      return;
    }

    prevEndTimeOptions.current = timeOptions?.end;
    autoSelectEndTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOptions?.end, minHours]);

  const requestTimeOptions = useCallback(
    async (date: Date, startTimeIndex?: number) => {
      if (getTimeOptions) {
        const isStartTimes = startTimeIndex === undefined;

        const newOptions = await getTimeOptions(date, startTimeIndex);
        setTimeOptions((prev) => ({
          ...prev,
          [isStartTimes ? "start" : "end"]: newOptions,
        }));

        if (isStartTimes) {
          setIsFetchingStartTimeOptions(false);
        } else {
          setIsFetchingEndTimeOptions(false);
        }
        return newOptions;
      }

      return TimeData;
    },
    [getTimeOptions],
  );

  const debouncedRequestTimeOptions = useMemo(() => {
    return debounce(requestTimeOptions, 500);
  }, [requestTimeOptions]);

  const updateEndDate = (
    startDate: BookingScheduleDate | null,
    endTime: BookingScheduleTime | null,
  ) => {
    if (!endTime) {
      // Clear end date if end time is cleared
      setValue(fieldNames.endDate, null);
      return;
    }

    if (!startDate) {
      setValue(fieldNames.endDate, null);
      return;
    }

    const endDate = endTime.overnight
      ? dateToBookingSchedule(addDays(bookingScheduleToDate(startDate!)!, 1))
      : startDate;
    setValue(fieldNames.endDate, endDate);
  };

  const handleStartDateChange = (date: Date) => {
    if (!date) {
      setCalendarPopoverOpen(false);
      return;
    }

    const startDate = dateToBookingSchedule(date);
    setValue(fieldNames.startDate, startDate);
    updateEndDate(startDate, watch(fieldNames.endTime));

    if (getTimeOptions) {
      setIsFetchingStartTimeOptions(true);
      debouncedRequestTimeOptions(date);
    }
    setCalendarPopoverOpen(false);
  };

  const handleStartTimeChange = (time: BookingScheduleTime | null) => {
    if (getTimeOptions) {
      setIsFetchingEndTimeOptions(true);
      requestTimeOptions(
        bookingScheduleToDate(watch(fieldNames.startDate))!,
        time?.time_index,
      );
    } else if (minHours) {
      autoSelectEndTime();
    }
  };

  const handleTimeChange = (
    fieldName: string,
    timeObj: BookingScheduleTime | null,
  ) => {
    if (fieldName === fieldNames.startTime) {
      handleStartTimeChange(timeObj);
    }

    if (fieldName === fieldNames.endTime) {
      updateEndDate(watch(fieldNames.startDate), timeObj);
    }
  };

  const currentDateValue = watch(fieldNames.startDate) as BookingScheduleDate;

  useEffect(() => {
    (async () => {
      if (!getTimeOptions) {
        // select components will display all time options if getTimeOptions is not provided
        return;
      }

      // Fetch start time options if start date exists on mount
      if (validateScheduleDate(currentDateValue)) {
        setIsFetchingStartTimeOptions(true);
        const startTimeOptions = await requestTimeOptions(
          bookingScheduleToDate(currentDateValue)!,
        );

        // Fetch end time options if start time exists and available on mount
        if (
          validateScheduleTime(watch(fieldNames.startTime)) &&
          startTimeOptions.find(
            (time) => time.time === watch(fieldNames.startTime)?.time,
          )?.available
        ) {
          setIsFetchingEndTimeOptions(true);
          await requestTimeOptions(
            bookingScheduleToDate(currentDateValue)!,
            watch(fieldNames.startTime)?.time_index,
          );
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-16"
      data-testing-id={dayPickerDataTestingId}
    >
      <DatePickerField
        fullWidth={true}
        name={fieldNames.startDate}
        popoverProps={{
          onOpenChange: handleOpenChange,
          open: calendarPopoverOpen,
        }}
        onValueChange={(date) => handleStartDateChange(date as Date)}
        testId={testId ? `${testId}-datepicker` : undefined}
        {...(datePickerProps || {})}
        calendarProps={{
          numberOfMonths: (windowDims.width || 0) < Breakpoints.SM ? 1 : 2,
          onMonthChange: handleMonthChange,
          locale,
          components: {
            DayContent: (props) => {
              return <CalendarAvailabilityDayContent {...props} />;
            },
          },
          modifiers: {
            ...(datePickerProps?.calendarProps?.modifiers || {}),
            ...(calendarModifiers || {}),
            disabled: {
              before: new Date(),
            },
          },
          showLoading: isFetchingCalendarModifiers,
          defaultMonth: currentDateValue
            ? new Date(
                currentDateValue.y as number,
                (currentDateValue.m as number) - 1,
              )
            : new Date(),
          ...(datePickerProps?.calendarProps || {}),
        }}
      />

      <TimeSelectField
        fullWidth={true}
        testId={testId}
        timeOptions={timeOptions}
        {...(timeSelectProps || {})}
        onValueChange={(fieldName, timeObj) => {
          handleTimeChange(fieldName, timeObj);
        }}
        startTimeProps={{
          name: fieldNames.startTime,
          ...(timeSelectProps?.startTimeProps || {}),
          disabled:
            timeSelectProps?.startTimeProps?.disabled ||
            !currentDateValue ||
            isFetchingStartTimeOptions,
        }}
        endTimeProps={{
          name: fieldNames.endTime,
          ...(timeSelectProps?.endTimeProps || {}),
          readOnly: !!minHours,
          disabled:
            timeSelectProps?.endTimeProps?.disabled ||
            !watch(fieldNames.startTime) ||
            !currentDateValue ||
            isFetchingEndTimeOptions ||
            isFetchingStartTimeOptions,
        }}
      />
    </div>
  );
};
